import {createSlice, PayloadAction} from '@reduxjs/toolkit';

export type KycStatus =
    | 'NOT_VERIFIED'
    | 'NOT_STARTED'
    | 'PENDING'
    | 'REVIEW'
    | 'RENEW'
    | 'APPROVED'
    | 'DECLINED'
    | null;

export interface SteamAccount {
    id: string;
    steamId: string;
    profileName: string | null;
    profileSteamUrl: string | null;
    avatarUrl: string | null;
    tradeUrl?: string | null;
    escrowDays?: number | null;
    escrow?: boolean | null;
    isValid?: boolean | null;
    status?: string | null;
    tradeable?: boolean;
    favoriteAccount?: boolean;
}

export interface UserSyncDTO {
    hasMfa: boolean;
    kycStatus: KycStatus;
    role: string | null;
    userMail: string | null;
    signUpStep: string;
    away: boolean;
    currency: string;
    language: string;
    extensionSetupAt: string | null;
}

export interface WalletDTO {
    walletStatus: 'AVAILABLE' | 'WAITING_FOR_KYC' | 'FROZEN' | 'BANNED' | 'DELETED' | 'INACTIVE' | null;
    walletTotalAmountMinor: number | null;
    walletTradeableAmountMinor: number | null;
    walletWithdrawableAmountMinor: number | null;
    walletOnHoldSaleAmountMinor: number | null;
    walletOnHoldPurchaseAmountMinor: number | null;
    walletOnHoldDepositAmountMinor: number | null;
    walletTotalRevenueMinor: number | null;
    walletTotalPurchaseMinor?: number | null;
}

export interface FeesDTO {
    fee: number | null;
    withdrawFee: number | null;
    hasPremium: boolean | null;
}

export interface PaymentAccountsDTO {
    stripe: StripeDTO;
}

export interface StripeDTO {
    stripeConnectEnabled: boolean,
}

export interface StatisticsDTO {
    totalTrades: number;
    totalSales: number;
    totalPurchases: number;
    totalAvoidedTrades: number;
    totalFailedTrades: number;
    totalVerifiedTrades: number;
    medianTradeTime: number;
    pendingOffers: number;
    actionableTrades: number;
    totalUnreadNotifications: number;
}

export interface FcmDTO {
    device: string | null;
    creationDate: string | null;
}

export interface SyncDTO {
    user: UserSyncDTO;
    wallet: WalletDTO;
    fees: FeesDTO;
    paymentAccounts: PaymentAccountsDTO;
    statistics: StatisticsDTO;
    fcm: FcmDTO;
    selectedSteamAccount: SteamAccount | null;
    steamAccounts: SteamAccount[] | null;
}

export interface AuthState {
    isAuthenticated: boolean | null;
    isMfaRequired: boolean;
    userId: bigint | null;
    userMail: string | null;
    role: string | null;
    kycStatus: KycStatus;
    signUpStep: string;
    steamAccountAmount: number;
    isEarningsHidden: boolean;
    user: UserSyncDTO | null;
    wallet: WalletDTO | null;
    fees: FeesDTO | null;
    paymentAccounts: PaymentAccountsDTO | null;
    statistics: StatisticsDTO | null;
    fcm: FcmDTO | null;
    selectedSteamAccount: SteamAccount | null;
    steamAccounts: SteamAccount[];
}

const initialState: AuthState = {
    isAuthenticated: null,
    isMfaRequired: false,
    userId: null,
    userMail: null,
    role: null,
    kycStatus: null,
    signUpStep: 'NONE',
    steamAccountAmount: 0,
    isEarningsHidden: false,
    user: null,
    wallet: null,
    fees: null,
    paymentAccounts: null,
    statistics: null,
    fcm: null,
    selectedSteamAccount: null,
    steamAccounts: [],
};

const saveToLocalStorage = (isAuthenticated: boolean | null, accounts: SteamAccount[], selected: SteamAccount | null) => {
    try {
        localStorage.setItem('isAuthenticated', JSON.stringify(isAuthenticated));
        localStorage.setItem('steamAccounts', JSON.stringify(accounts));
        localStorage.setItem('selectedSteamAccount', JSON.stringify(selected));
    } catch (e) {
        console.error('Failed to save steam accounts to localStorage', e);
    }
};

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setAuthState: (state, action: PayloadAction<Partial<AuthState>>) => {
            const updatedState = {...state, ...action.payload};
            if (action.payload.steamAccounts) {
                updatedState.steamAccountAmount = action.payload.steamAccounts.length;
            }
            saveToLocalStorage(updatedState.isAuthenticated, updatedState.steamAccounts, updatedState.selectedSteamAccount);
            return updatedState;
        },
        setMfaRequired: (state, action: PayloadAction<boolean>) => {
            state.isMfaRequired = action.payload;
            if (action.payload) {
                state.isAuthenticated = false;
            }
        },
        setWalletState: (state, action: PayloadAction<WalletDTO | null>) => {
            state.wallet = action.payload;
        },
        toggleEarningsVisibility: (state) => {
            state.isEarningsHidden = !state.isEarningsHidden;
        },
        setSignUpStep: (state, action: PayloadAction<string>) => {
            state.signUpStep = action.payload;
        },
        updateSelectedAccount: (state, action: PayloadAction<SteamAccount | null>) => {
            state.selectedSteamAccount = action.payload;
            saveToLocalStorage(state.isAuthenticated, state.steamAccounts, state.selectedSteamAccount);
        },
        subtractFromWalletAfterBuy: (state, action: PayloadAction<number>) => {
            const price = action.payload;
            if (state.wallet) {
                if (state.wallet.walletTradeableAmountMinor !== null) {
                    state.wallet.walletTradeableAmountMinor -= price;
                }
                if (state.wallet.walletWithdrawableAmountMinor !== null) {
                    state.wallet.walletWithdrawableAmountMinor -= price;
                }
                if (state.wallet.walletOnHoldPurchaseAmountMinor !== null) {
                    state.wallet.walletOnHoldPurchaseAmountMinor += price;
                }
            }
        },
        applyWalletSpend: (state, action: PayloadAction<number>) => {
            const amount = action.payload;
            if (!state.wallet || state.wallet.walletTradeableAmountMinor === null || state.wallet.walletWithdrawableAmountMinor === null) return;
            const tradeableBefore = state.wallet.walletTradeableAmountMinor;
            const withdrawableBefore = state.wallet.walletWithdrawableAmountMinor;
            const lockedBefore = tradeableBefore - withdrawableBefore;
            state.wallet.walletTradeableAmountMinor -= amount;
            if (state.wallet.walletTotalAmountMinor !== null) {
                state.wallet.walletTotalAmountMinor -= amount;
            }
            const lockedAfter = Math.max(0, lockedBefore - amount);
            state.wallet.walletWithdrawableAmountMinor = state.wallet.walletTradeableAmountMinor - lockedAfter;
            if (state.wallet.walletWithdrawableAmountMinor < 0) {
                state.wallet.walletWithdrawableAmountMinor = 0;
            }
        },
        clearUnreadNotifications: (state) => {
            if (state.statistics) {
                state.statistics.totalUnreadNotifications = 0;
            }
        },
        logout: (state) => {
            saveToLocalStorage(null, [], null);
            return {...initialState, isAuthenticated: false};
        },
        signOut: (state) => {
            saveToLocalStorage(null, [], null);
            return {...initialState, isAuthenticated: false};
        }
    },
});

export const {
    setAuthState,
    setMfaRequired,
    setWalletState,
    toggleEarningsVisibility,
    setSignUpStep,
    updateSelectedAccount,
    subtractFromWalletAfterBuy,
    applyWalletSpend,
    clearUnreadNotifications,
    logout,
    signOut
} = authSlice.actions;

export default authSlice.reducer;